import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import {
  ArrowLeftIcon,
  ArrowDownTrayIcon,
  ChatBubbleLeftRightIcon,
  ChevronDownIcon,
  DocumentIcon,
  EyeSlashIcon,
  PhotoIcon,
  TableCellsIcon,
  VideoCameraIcon,
} from "@heroicons/react/24/outline";
import cn from "classnames";
import AiDisclaimer from "./AiDisclaimer";
import Button from "../common/Button";
import SlideDrawer from "../common/SlideDrawer";
import ResizeHandle from "../common/ResizeHandle";
import { useResizablePanel } from "../../hooks/useResizablePanel";
import { revealProps } from "../common/Reveal";
import { DocLeadAnchor, SpreadsheetLeadAnchor } from "../../db/schema";
import { api, DISMISS_REASONS, FileDetailData, FileInsightsMetadata, LEAD_CATEGORY_LABELS, SEVERITY_ACTIVE_CLASSES, SEVERITY_CLASSES } from "./api";
import InvestigationChat from "./InvestigationChat";
import TriageButton from "./TriageButton";
import PdfFileViewer, { PdfViewerHandle } from "./viewers/PdfFileViewer";
import SpreadsheetViewer, { SpreadsheetViewerHandle } from "./viewers/SpreadsheetViewer";
import TextFileViewer, { TextViewerHandle } from "./viewers/TextFileViewer";
import PhotoViewer, { PhotoViewerHandle } from "./viewers/PhotoViewer";
import VideoFileViewer from "./viewers/VideoFileViewer";

const KIND_ICON: Record<string, React.FC<{ className?: string }>> = { document: DocumentIcon, spreadsheet: TableCellsIcon, photo: PhotoIcon, video: VideoCameraIcon };
const KIND_LABEL: Record<string, string> = { document: "Document", spreadsheet: "Spreadsheet", photo: "Photo", jail_call: "Jail call", video: "Video" };
const STATUS_LABELS: Record<string, string> = { pending: "Queued", uploading: "Uploading", processing: "Analyzing", completed: "Analyzed", failed: "Failed" };

/**
 * Per-file detail: the viewer for the file's kind on the left, and the
 * insights the pipeline already computed for it on the right — the file
 * equivalent of CallDetail, lighter because documents have no timeline.
 * Jail calls redirect to their CallDetail.
 */
const FileDetail: React.FC = () => {
  const { investigationId = "", fileId = "" } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const goBack = () => {
    if (location.key !== "default") navigate(-1);
    else navigate(`/investigations/${investigationId}/files`);
  };
  const [file, setFile] = useState<FileDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [chatOpen, setChatOpen] = useState(false);
  const [selectedTableId, setSelectedTableId] = useState<string | null>(null);
  const [ocrOpen, setOcrOpen] = useState(false);
  const [rerunning, setRerunning] = useState(false);
  const viewerRef = useRef<PdfViewerHandle>(null);
  const sheetRef = useRef<SpreadsheetViewerHandle>(null);
  // Two-way highlight between the sheet grid and the flag cards, mirroring
  // CallDetail's transcript <-> rail interplay: hovering a card highlights
  // its rows (activeLeadId), hovering a flagged row lights its cards.
  const [activeLeadId, setActiveLeadId] = useState<string | null>(null);
  const [gridHoverIds, setGridHoverIds] = useState<Set<string>>(new Set());
  const initialPage = params.get("page") ? Number(params.get("page")) : undefined;
  const initialSeekMs = params.get("t") ? Number(params.get("t")) : undefined;
  const focusLeadParam = params.get("lead");
  const focusedLead = useRef(false);
  const insightsPanel = useResizablePanel({
    storageKey: "pnai-fileInsightsWidth",
    defaultWidth: 384,
    minWidth: 260,
    maxWidth: 720,
    side: "left",
    maxViewportFraction: 0.5,
  });

  const load = useCallback(async () => {
    try {
      const data = await api.file(investigationId, fileId);
      if (data.kind === "jail_call") {
        if (data.callId) navigate(`/investigations/${investigationId}/calls/${data.callId}`, { replace: true });
        else navigate(`/investigations/${investigationId}/calls`, { replace: true });
        return;
      }
      setFile(data);
      setSelectedTableId((prev) => prev || data.tables[0]?.id || null);
    } catch {
      toast.error("Failed to load file");
    } finally {
      setLoading(false);
    }
  }, [investigationId, fileId, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  // ?lead= deep link (brief timeline, chat): once the file is in, drive the
  // same focus machinery the rail uses — highlight and jump to the anchor.
  useEffect(() => {
    if (!file || !focusLeadParam || focusedLead.current) return;
    const l = file.leads.find((x) => x.id === focusLeadParam);
    if (!l) return;
    focusedLead.current = true;
    setActiveLeadId(l.id);
    if (file.kind === "video") return;
    const t = setTimeout(() => {
      if (l.sheet_anchor) sheetRef.current?.focusLead(l);
      else {
        const page = (l.doc_anchor as DocLeadAnchor | null)?.page || l.page;
        if (page) viewerRef.current?.goToPage(page);
      }
    }, 60);
    return () => clearTimeout(t);
  }, [file, focusLeadParam]);

  // Poll while the pipeline is still working on this file.
  useEffect(() => {
    if (!file) return;
    const videoBusy = file.kind === "video" && file.video && !["completed", "failed"].includes(file.video.status);
    if (["completed", "failed"].includes(file.processing_status || "") && !videoBusy) return;
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
  }, [file, load]);

  // A presigned URL lives an hour; if the viewer's fetch dies, mint a fresh
  // one once instead of leaving a broken pane.
  const refreshedUrl = useRef(false);
  const onViewerError = () => {
    if (refreshedUrl.current) return;
    refreshedUrl.current = true;
    load();
  };

  const download = async () => {
    if (!file) return;
    try {
      const url = await api.fileUrl(investigationId, file.id);
      window.open(url, "_blank", "noopener");
    } catch {
      toast.error("Failed to get download link");
    }
  };

  const reclassify = async (source_class: "official" | "evidence") => {
    if (!file || file.source_class === source_class) return;
    (document.activeElement as HTMLElement | null)?.blur();
    try {
      await api.setFileClass(investigationId, file.id, source_class);
      setFile({ ...file, source_class });
    } catch {
      toast.error("Failed to reclassify");
    }
  };

  const rerunAnalysis = async () => {
    setRerunning(true);
    try {
      await api.retryFile(investigationId, fileId);
      await load();
    } catch {
      toast.error("Failed to start re-analysis");
    } finally {
      setRerunning(false);
    }
  };

  const setLeadStatus = async (leadId: string, status: string, dismiss_reason?: string) => {
    if (!file) return;
    try {
      const updated = await api.updateLead(investigationId, leadId, { status, ...(dismiss_reason ? { dismiss_reason } : {}) });
      setFile({ ...file, leads: file.leads.map((l) => (l.id === leadId ? updated : l)) });
    } catch {
      toast.error("Failed to update lead");
    }
  };

  if (loading) {
    return (
      <div className="flex-1 overflow-auto p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-base-300 rounded w-1/3" />
          <div className="h-64 bg-base-300 rounded" />
        </div>
      </div>
    );
  }
  if (!file) return <div className="flex-1 p-6">File not found.</div>;

  const meta = (file.metadata || {}) as FileInsightsMetadata;
  const privileged = Boolean(file.privileged_at);
  const status = privileged ? "privileged" : file.processing_status || "pending";
  const analyzed = status === "completed";
  const Icon = KIND_ICON[file.kind] || DocumentIcon;
  const activeLeads = file.leads.filter((l) => l.status !== "dismissed");
  // Documents and photos both get the official/evidence read; spreadsheets
  // are always third-party records.
  const classified = file.kind === "document" || file.kind === "photo";
  // The findings were produced under metadata.analyzedClass; a differing
  // source_class means they no longer match how the file should be read.
  // Server-derived, so it survives navigation and flipping the class back
  // clears it. Files analyzed before analyzedClass existed never show it.
  const needsRerun =
    classified && analyzed && !!meta.analyzedClass && !!file.source_class && file.source_class !== meta.analyzedClass;
  const uniqueMentions = [...new Map(file.mentions.map((m) => [m.entity.id, m])).values()];
  const selectedTable = file.tables.find((t) => t.id === selectedTableId) || file.tables[0] || null;

  const subline = [
    KIND_LABEL[file.kind] || file.kind,
    file.kind === "video" && file.video?.durationSec ? `${Math.floor(file.video.durationSec / 60)} min${file.video.hasAudio === false ? ", no audio" : ""}` : null,
    file.file_size,
    file.created_at ? `added ${new Date(file.created_at).toLocaleDateString()}` : null,
    file.kind === "document" && meta.pageCount ? `${meta.pageCount} page${meta.pageCount === 1 ? "" : "s"}` : null,
    file.kind === "spreadsheet" && meta.rowCount
      ? `${meta.rowCount.toLocaleString()} rows${meta.sheets && meta.sheets.length > 1 ? ` across ${meta.sheets.length} sheets` : ""}`
      : null,
  ]
    .filter(Boolean)
    .join(" · ");

  const viewer = privileged ? (
    <div className="p-6 text-base-content/70">
      <div className="flex items-center gap-2 font-medium text-base-content">
        <EyeSlashIcon className="size-5" /> Marked privileged
      </div>
      <p className="mt-2 max-w-lg">
        This file was marked attorney-client privileged. Its content and AI analysis have been removed from search, chat, and this
        screen; the file itself is retained for chain of custody.
      </p>
    </div>
  ) : !file.url ? (
    <div className="p-6 text-base-content/70">Preparing preview…</div>
  ) : file.kind === "photo" ? (
    <PhotoViewer
      ref={viewerRef as unknown as React.Ref<PhotoViewerHandle>}
      url={file.url}
      alt={file.original_file_name || "Photo"}
      onLoadError={onViewerError}
      leads={file.leads}
      activeLeadId={activeLeadId}
      onHoverLeads={(ids) => setGridHoverIds(new Set(ids))}
    />
  ) : file.kind === "spreadsheet" ? (
    file.tables.length > 0 ? (
      <SpreadsheetViewer
        ref={sheetRef}
        investigationId={investigationId}
        tables={file.tables}
        selectedTableId={selectedTableId}
        onSelectTable={setSelectedTableId}
        leads={file.leads}
        activeLeadId={activeLeadId}
        onHoverLeads={(ids) => setGridHoverIds(new Set(ids))}
      />
    ) : (
      <div className="p-6 text-base-content/70">
        {status === "failed" ? "This spreadsheet could not be parsed." : "Parsing the spreadsheet…"}
        <div className="mt-3">
          <Button variant="secondary" size="sm" onClick={download}>
            <ArrowDownTrayIcon className="h-4 w-4 mr-1" /> Open the original
          </Button>
        </div>
      </div>
    )
  ) : (
    // Documents. Non-PDFs (docx, txt) can't render inline; offer the original.
    /\.pdf$/i.test(file.file_name || "") || file.file_type === "application/pdf" ? (
      <PdfFileViewer
        ref={viewerRef}
        url={file.url}
        initialPage={initialPage}
        onLoadError={onViewerError}
        leads={file.leads}
        activeLeadId={activeLeadId}
        onHoverLeads={(ids) => setGridHoverIds(new Set(ids))}
      />
    ) : (
      // Word / plain text: the server extracted the text into sections at
      // upload time; render those with the same lead highlighting the PDF
      // overlay gives. The original stays one click away.
      <div>
        <div className="px-4 pt-3 flex items-center justify-between text-xs text-base-content/60">
          <span>Text extracted from {file.original_file_name}</span>
          <button type="button" className="text-blue-600 dark:text-blue-300 hover:underline inline-flex items-center gap-1" onClick={download}>
            <ArrowDownTrayIcon className="size-3.5" /> Open the original
          </button>
        </div>
        <TextFileViewer
          ref={viewerRef as unknown as React.Ref<TextViewerHandle>}
          investigationId={investigationId}
          fileId={file.id}
          initialPage={initialPage}
          onLoadError={onViewerError}
          leads={file.leads}
          activeLeadId={activeLeadId}
          onHoverLeads={(ids) => setGridHoverIds(new Set(ids))}
        />
      </div>
    )
  );

  return (
    <div className="flex-1 flex min-h-0">
      <div className="flex-1 flex flex-col min-w-0">
        <div className="px-4 py-3 border-b border-base-300 flex items-center gap-3 flex-wrap">
          <Button variant="secondary" size="sm" onClick={goBack}>
            <ArrowLeftIcon className="h-4 w-4 mr-1" /> Back
          </Button>
          <div className="min-w-0 flex-1">
            <h1 className="font-semibold text-base-content truncate flex items-center gap-2">
              <Icon className="size-4 text-base-content/50 shrink-0" />
              <span className="truncate">{file.original_file_name}</span>
            </h1>
            <p className="text-xs text-base-content/60">{subline}</p>
          </div>
          {status === "privileged" ? (
            <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-base-200 text-base-content/60">Privileged</span>
          ) : status === "failed" ? (
            <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-800 dark:bg-red-950/40 dark:text-red-400" title={file.error_message || ""}>
              Failed
            </span>
          ) : !analyzed || !classified ? (
            <span
              className={cn(
                "px-2 py-0.5 rounded-full text-xs font-semibold",
                analyzed
                  ? "bg-green-100 text-green-800 dark:bg-green-950/40 dark:text-green-400"
                  : "bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-400"
              )}
            >
              {STATUS_LABELS[status] || status}
            </span>
          ) : null}
          {/* Documents and photos: the official/evidence classification,
              correctable in place. Official material is read as context
              (discrepancies + new info), not mined for threat flags. */}
          {classified && !privileged && status !== "failed" && (analyzed || file.source_class) && (
            <div className="dropdown dropdown-end">
              <button
                tabIndex={0}
                className={cn(
                  "px-2 py-0.5 rounded-full text-xs font-semibold inline-flex items-center gap-1 capitalize",
                  file.source_class === "official"
                    ? "bg-purple-50 text-purple-700 dark:bg-purple-950/40 dark:text-purple-300"
                    : "bg-blue-50 text-blue-600 dark:bg-blue-900/40 dark:text-blue-300"
                )}
                title={`How this ${file.kind === "photo" ? "photo" : "document"} is read. Click to change.`}
              >
                {file.source_class === "official" ? `Case document${meta.documentType ? ` · ${meta.documentType}` : ""}` : meta.documentType || "Evidence"}
                <ChevronDownIcon className="size-3 opacity-60" />
              </button>
              <ul tabIndex={0} className="dropdown-content menu bg-base-100 rounded-lg z-[30] w-72 p-2 shadow border border-base-300 mt-1 normal-case">
                {(
                  [
                    ["official", "Our case material", "A report, filing, record, or scene photo from the case file. Read as authoritative context; flagged only for discrepancies and new information."],
                    ["evidence", "Third-party evidence", "Material authored or captured outside law enforcement. Mined for leads like the calls are."],
                  ] as const
                ).map(([value, label, hint]) => (
                  <li key={value}>
                    <button className="flex flex-col items-start gap-0 hover:bg-gray-100 dark:hover:bg-base-200" onClick={() => reclassify(value)}>
                      <span className={cn("text-sm", file.source_class === value ? "font-semibold text-base-content" : "text-base-content")}>
                        {label}
                        {file.source_class === value ? " ✓" : ""}
                      </span>
                      <span className="text-[11px] text-base-content/50 text-left">{hint}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {!privileged && (
            <Button variant="secondary" size="sm" onClick={download} title="Open the original file">
              <ArrowDownTrayIcon className="h-4 w-4 mr-1" /> Download
            </Button>
          )}
          <Button variant={chatOpen ? "primary" : "secondary"} size="sm" onClick={() => setChatOpen((o) => !o)}>
            <ChatBubbleLeftRightIcon className="h-4 w-4 mr-1" /> Ask
          </Button>
        </div>

        {file.kind === "video" && !privileged ? (
          <div className="flex-1 min-h-0">
            <VideoFileViewer
              investigationId={investigationId}
              file={file}
              initialSeekMs={initialSeekMs}
              focusLeadId={focusLeadParam}
              onLeadStatus={setLeadStatus}
              onRerun={rerunAnalysis}
              rerunning={rerunning}
              onFileChange={setFile}
            />
          </div>
        ) : (
        <div className="flex-1 flex min-h-0">
          <div className="flex-1 min-w-0 border-r border-base-300 min-h-0">{viewer}</div>

          <div style={{ width: insightsPanel.width }} className="relative shrink-0 flex min-h-0">
            <ResizeHandle edge="left" active={insightsPanel.isResizing} {...insightsPanel.handleProps} />
            <aside className="flex-1 min-w-0 overflow-auto p-4 space-y-5 text-sm bg-base-200/40">
              {privileged ? (
                <p className="text-base-content/60">AI insights were removed when this file was marked privileged.</p>
              ) : (
                <>
                  {status === "failed" && (
                    <section className="rounded-lg border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/40 p-3 text-red-800 dark:text-red-400">
                      {file.error_message || "Processing failed."}
                    </section>
                  )}
                  {needsRerun && (
                    <section className="rounded-lg border border-purple-200 dark:border-purple-900 bg-purple-50/60 dark:bg-purple-950/20 p-3">
                      <p className="text-[13px] leading-5 text-base-content/80">
                        Reclassified. The findings below were produced under the old classification and were kept — re-run the
                        analysis to re-read this {file.kind === "photo" ? "photo" : "document"} under the new one.
                      </p>
                      <Button variant="secondary" size="sm" className="mt-2" disabled={rerunning} onClick={rerunAnalysis}>
                        {rerunning ? "Starting…" : "Re-run analysis"}
                      </Button>
                    </section>
                  )}
                  {(meta.description || (file.kind === "spreadsheet" && selectedTable?.summary)) && (
                    <section>
                      <h3 className="font-semibold text-base-content mb-1">Summary</h3>
                      <AiDisclaimer kind="documentSummary" size="sm" className="mb-2" />
                      <p className="text-base-content/80 whitespace-pre-wrap">{meta.description || selectedTable?.summary}</p>
                      {/* The analyst note above; the computed sheet stats below,
                          minus the chat-tool plumbing lines. */}
                      {file.kind === "spreadsheet" && meta.description && selectedTable?.summary && (
                        <p className="mt-2 text-xs text-base-content/60 whitespace-pre-wrap">
                          {selectedTable.summary
                            .split("\n")
                            .filter((l) => !l.startsWith("Use the query_spreadsheet") && !l.startsWith("Sample rows:"))
                            .join("\n")}
                        </p>
                      )}
                      {file.kind === "document" && meta.pageCount != null && (
                        <p className="mt-1 text-xs text-base-content/60">
                          {meta.pageCount} page{meta.pageCount === 1 ? "" : "s"} read
                          {meta.extractedChars ? ` · ${meta.extractedChars.toLocaleString()} characters of text` : ""}
                        </p>
                      )}
                    </section>
                  )}
                  {!analyzed && !meta.description && status !== "failed" && (
                    <section>
                      <h3 className="font-semibold text-base-content mb-1">Summary</h3>
                      <p className="text-base-content/60">
                        <span className="inline-block size-2 rounded-full bg-amber-500 animate-pulse mr-1.5" />
                        {STATUS_LABELS[status] || "Analyzing"}… insights appear when the file has been read.
                      </p>
                    </section>
                  )}

                  {file.kind === "spreadsheet" && selectedTable && (
                    <section>
                      <h3 className="font-semibold text-base-content mb-1">Detected columns</h3>
                      <ul className="text-xs text-base-content/70 space-y-0.5">
                        {(["date", "amount", "counterparty"] as const).map((role) =>
                          selectedTable.column_roles?.[role] ? (
                            <li key={role}>
                              <span className="capitalize text-base-content/50">{role}:</span>{" "}
                              <span className="font-medium text-base-content/80">{selectedTable.column_roles[role]}</span>
                            </li>
                          ) : null
                        )}
                      </ul>
                      <p className="mt-1.5 text-[11px] text-base-content/50">
                        These columns power the chat's computed answers (totals, date ranges, everything to a name).
                      </p>
                    </section>
                  )}

                  {classified && meta.keyDates && meta.keyDates.length > 0 && (
                    <section>
                      <h3 className="font-semibold text-base-content mb-1">Key dates</h3>
                      <AiDisclaimer kind="keyDates" size="sm" className="mb-2" />
                      <ul className="divide-y divide-base-300 rounded-lg border border-base-300 bg-base-100">
                        {meta.keyDates.map((d, i) => (
                          <li key={i}>
                            <button
                              type="button"
                              disabled={!d.page}
                              onClick={() => d.page && viewerRef.current?.goToPage(d.page)}
                              className={cn("w-full text-left px-3 py-2 transition-colors", d.page ? "hover:bg-base-200/70" : "cursor-default")}
                              title={d.page ? `Go to page ${d.page}` : undefined}
                            >
                              <div className="flex items-baseline gap-2">
                                <span className="font-semibold text-base-content leading-snug whitespace-nowrap">{d.date}</span>
                                {d.page && <span className="ml-auto shrink-0 text-[11px] tabular-nums text-blue-600 dark:text-blue-300">p. {d.page}</span>}
                              </div>
                              <p className="mt-0.5 text-[13px] leading-5 text-base-content/75">{d.description}</p>
                            </button>
                          </li>
                        ))}
                      </ul>
                    </section>
                  )}

                  {(file.leads.length > 0 || file.kind === "document" || ((file.kind === "spreadsheet" || file.kind === "photo") && meta.analyzedAt)) && (
                    <section>
                      <h3 className="font-semibold text-base-content mb-1">
                        {file.source_class === "official"
                          ? `Discrepancies & new information (${activeLeads.length})`
                          : `Flagged in this file (${activeLeads.length})`}
                      </h3>
                      {file.leads.length > 0 && <AiDisclaimer kind="documentFlags" size="sm" className="mb-2" />}
                      {file.leads.length === 0 && (
                        <p className="text-base-content/60">{analyzed ? "Nothing flagged." : "Pending analysis."}</p>
                      )}
                      <ul className="-mb-2">
                        {file.leads.map((l, i) => {
                          const anchor = file.kind === "spreadsheet" ? ((l.sheet_anchor as SpreadsheetLeadAnchor | null) || null) : null;
                          const docAnchor = classified ? ((l.doc_anchor as DocLeadAnchor | null) || null) : null;
                          const anchored = Boolean(anchor?.rows?.length || anchor?.column || docAnchor?.boxes?.length);
                          const gridActive = gridHoverIds.has(l.id) && l.status !== "dismissed";
                          return (
                          <li key={l.id} style={revealProps(i).style} className="reveal pb-2">
                            <div
                              onClick={() =>
                                anchor
                                  ? sheetRef.current?.focusLead(l)
                                  : (docAnchor?.page || l.page) && viewerRef.current?.goToPage(docAnchor?.page || l.page!)
                              }
                              onMouseEnter={anchored ? () => setActiveLeadId(l.id) : undefined}
                              onMouseLeave={anchored ? () => setActiveLeadId(null) : undefined}
                              title={
                                anchor
                                  ? "Highlight in the sheet"
                                  : anchored
                                    ? file.kind === "photo"
                                      ? "Highlight on the photo"
                                      : "Highlight on the page"
                                    : l.page
                                      ? `Go to page ${l.page}`
                                      : undefined
                              }
                              className={cn(
                                "rounded-lg border p-2 transition-colors",
                                (l.page || anchored) && "cursor-pointer",
                                gridActive
                                  ? cn("shadow-sm", SEVERITY_ACTIVE_CLASSES[l.severity] || SEVERITY_ACTIVE_CLASSES[3])
                                  : cn(
                                      "border-base-300 bg-base-100",
                                      (l.page || anchored) &&
                                        "hover:border-red-300 dark:hover:border-red-800 hover:bg-red-50/60 dark:hover:bg-red-950/30"
                                    ),
                                l.status === "dismissed" && "opacity-50"
                              )}
                            >
                              <div className="flex items-center gap-1 flex-wrap">
                                <span className={cn("text-[11px] font-semibold px-1.5 rounded", SEVERITY_CLASSES[l.severity])}>sev {l.severity}</span>
                                <span className="text-xs font-medium">{LEAD_CATEGORY_LABELS[l.category]}</span>
                                {l.page && (
                                  <button
                                    className="ml-auto text-xs text-blue-600 dark:text-blue-300 hover:underline"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      viewerRef.current?.goToPage(l.page!);
                                    }}
                                  >
                                    p. {l.page}
                                  </button>
                                )}
                                {anchor && (
                                  <button
                                    className="ml-auto text-xs text-blue-600 dark:text-blue-300 hover:underline"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      sheetRef.current?.focusLead(l);
                                    }}
                                  >
                                    {anchor.rows?.length
                                      ? `${anchor.rows.length} row${anchor.rows.length === 1 ? "" : "s"}`
                                      : anchor.column}
                                  </button>
                                )}
                              </div>
                              <p className="mt-1 italic text-base-content/80">"{l.quote_translated || l.quote}"</p>
                              <p className="text-xs text-base-content/60">{l.explanation}</p>
                              <div className="mt-2 flex items-center gap-1.5">
                                <TriageButton
                                  kind="confirm"
                                  active={l.status === "confirmed"}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setLeadStatus(l.id, l.status === "confirmed" ? "new" : "confirmed");
                                  }}
                                />
                                {l.status === "dismissed" ? (
                                  <TriageButton
                                    kind="restore"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setLeadStatus(l.id, "new");
                                    }}
                                  />
                                ) : (
                                  <div className="dropdown dropdown-bottom" onClick={(e) => e.stopPropagation()}>
                                    <TriageButton kind="dismiss" tabIndex={0} />
                                    <ul tabIndex={0} className="dropdown-content menu bg-base-100 rounded-lg z-[30] w-56 p-2 shadow border border-base-300 mt-1">
                                      {DISMISS_REASONS.map((r) => (
                                        <li key={r.value}>
                                          <button
                                            className="flex flex-col items-start gap-0 hover:bg-gray-100 dark:hover:bg-base-200"
                                            onClick={() => {
                                              setLeadStatus(l.id, "dismissed", r.value);
                                              (document.activeElement as HTMLElement | null)?.blur();
                                            }}
                                          >
                                            <span className="text-sm text-base-content">{r.label}</span>
                                            <span className="text-[11px] text-base-content/50">{r.hint}</span>
                                          </button>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                              </div>
                            </div>
                          </li>
                          );
                        })}
                      </ul>
                    </section>
                  )}

                  {file.kind === "photo" && meta.glossary && meta.glossary.length > 0 && (
                    <section>
                      <h3 className="font-semibold text-base-content mb-1">Coded language ({meta.glossary.length})</h3>
                      <AiDisclaimer kind="glossary" size="sm" className="mb-2" />
                      <ul className="divide-y divide-base-300 rounded-lg border border-base-300 bg-base-100">
                        {meta.glossary.map((g, i) => (
                          <li key={i} className="px-3 py-2">
                            <div className="flex items-baseline gap-2">
                              <span className="font-semibold text-base-content leading-snug">"{g.term}"</span>
                              <span className="ml-auto shrink-0 text-[11px] tabular-nums text-base-content/50">{Math.round((g.confidence || 0) * 100)}%</span>
                            </div>
                            <p className="mt-0.5 text-[13px] leading-5 text-base-content/75">{g.likelyMeaning}</p>
                            {g.quote && <p className="mt-0.5 text-xs italic text-base-content/50">"{g.quote}"</p>}
                          </li>
                        ))}
                      </ul>
                    </section>
                  )}

                  {file.kind === "photo" && meta.ocrText && (
                    <section>
                      <button
                        type="button"
                        onClick={() => setOcrOpen((o) => !o)}
                        className="w-full flex items-center gap-1 font-semibold text-base-content mb-1"
                      >
                        Text in this photo
                        <ChevronDownIcon className={cn("size-3.5 text-base-content/50 transition-transform", ocrOpen && "rotate-180")} />
                      </button>
                      <div className={cn("grid transition-[grid-template-rows] duration-300 ease-in-out", ocrOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]")}>
                        <div className="min-h-0 overflow-hidden">
                          <p className="whitespace-pre-wrap text-[13px] leading-5 text-base-content/75 rounded-lg border border-base-300 bg-base-100 px-3 py-2">
                            {meta.ocrText}
                          </p>
                        </div>
                      </div>
                    </section>
                  )}

                  {uniqueMentions.length > 0 && (
                    <section>
                      <h3 className="font-semibold text-base-content mb-1">People & things mentioned</h3>
                      <AiDisclaimer kind="entities" size="sm" className="mb-2" />
                      <ul className="flex flex-wrap gap-1">
                        {uniqueMentions.map((m) => (
                          <li key={m.entity.id}>
                            <Link
                              to={`/investigations/${investigationId}/people?entity=${m.entity.id}`}
                              state={{ back: "file" }}
                              className="text-xs px-2 py-0.5 rounded-full bg-base-200 hover:bg-base-300"
                            >
                              {m.entity.canonical_name} <span className="text-base-content/40">{m.entity.type}</span>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </section>
                  )}
                </>
              )}
            </aside>
          </div>
        </div>
        )}
      </div>

      <SlideDrawer open={chatOpen} storageKey="pnai-investigationChatWidth" className="border-l border-base-300 bg-base-100">
        <InvestigationChat investigationId={investigationId} compact onClose={() => setChatOpen(false)} />
      </SlideDrawer>
    </div>
  );
};

export default FileDetail;
